import React from 'react';
import { CommandBarButton, IconButton, Icon } from '@fluentui/react';
import DetailsTable from '../../../../Table/DetailsTable';
import FeatureSummary from '../../../../Util/FeatureSummary';

const CustomDomainTable = ({
    domains,
    setSelectedDomain,
    loading,
    editOnClick,
    createOnClick,
    previewOnClick,
    deleteOnClick,
}) => {
    const columns = [
        {
            minWidth: 30,
            maxWidth: 30,
            onRender: (item) => (
                <ContextualMenu editOnClick={editOnClick} previewOnClick={previewOnClick} id={item.cobrandedUrlId} item={item} deleteOnClick={deleteOnClick} />
            ),
        },
        {
            name: 'Cobrand',
            fieldName: 'cobrandName',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            isSorted: true,
            isSortedDescending: true,
        },
        {
            name: 'DomainMask',
            fieldName: 'domainMask',
            minWidth: 200,
            maxWidth: 250,
            isResizable: true,
            isSorted: true,
            isSortedDescending: true,
        },
        {
            name: 'Type',
            fieldName: 'domainType',
            minWidth: 140,
            maxWidth: 200,
            isResizable: true,
            onRender: (item) => {
                if (!item.isCustomHostname) {
                    return <span style={{ color: '#666' }}>Subdomain</span>;
                }

                switch (item.domainType) {
                    case 'CloudflareCustomHostname':
                        return (
                            <span style={{ color: '#0078d4' }}>
                                <Icon iconName="Globe" style={{ marginRight: 5 }} />
                                Own certificate
                            </span>
                        );
                    case 'WildcardChild':
                        return (
                            <span style={{ color: '#0078d4' }} title={`Covered by ${item.coveringWildcard}`}>
                                <Icon iconName="Globe" style={{ marginRight: 5 }} />
                                Under {item.coveringWildcard}
                            </span>
                        );
                    case 'ClientCertificate':
                        return <span style={{ color: '#666' }}>Client certificate</span>;
                    case 'Unresolved':
                        return (
                            <span
                                style={{ color: '#666' }}
                                title="Client domain not managed by Cloudflare - served with a certificate the client supplies, or a record that is no longer in use"
                            >
                                Client-managed
                            </span>
                        );
                    default:
                        return <span style={{ color: '#999' }}>Unknown</span>;
                }
            },
        },
        {
            name: 'Status',
            fieldName: 'dnsStatus',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            onRender: (item) => {
                if (!item.isCustomHostname || item.domainType === 'ClientCertificate' || item.domainType === 'Unresolved') {
                    return <span style={{ color: '#999' }}>N/A</span>;
                }
                if (item.statusError || !item.dnsStatus) {
                    return (
                        <span style={{ color: '#a4262c', fontWeight: 'bold' }} title={item.statusError}>
                            Unknown
                        </span>
                    );
                }
                const color = item.dnsStatus === 'active' ? 'green' : item.dnsStatus === 'pending' ? 'orange' : 'red';
                return (
                    <span style={{ color, fontWeight: 'bold', textTransform: 'capitalize' }}>
                        {item.dnsStatus}
                    </span>
                );
            },
        },
        {
            name: 'SSL',
            fieldName: 'sslStatus',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            onRender: (item) => {
                if (!item.isCustomHostname || item.domainType === 'ClientCertificate' || item.domainType === 'Unresolved') {
                    return <span style={{ color: '#999' }}>N/A</span>;
                }
                if (!item.sslStatus) {
                    return <span style={{ color: '#a4262c', fontWeight: 'bold' }}>Unknown</span>;
                }
                const color = item.sslStatus === 'active' ? 'green' : item.sslStatus === 'pending' ? 'orange' : 'red';
                return (
                    <span style={{ color, fontWeight: 'bold', textTransform: 'capitalize' }}>
                        {item.sslStatus}
                    </span>
                );
            },
        },
    ];

    const items = domains ?? [];

    const selectionHook = (items) => {
        const selectedItem = items && items[0];
        setSelectedDomain(selectedItem && selectedItem.cobrandedUrlId);
    };

    return (
        <>
        <DetailsTable
            items={items}
            columns={columns}
            selectionHook={selectionHook}
            loading={loading}
            mainColumn={'name'}
            style={{
                position: 'relative',
                height: '80vh',
                width: '80vw',
            }}
            CommandBar={
                <>
                    <CommandBarButton
                        iconProps={{ iconName: 'Edit' }}
                        text="Add Masked Domain"
                        onClick={() => createOnClick()}
                    />
                </>
            }
        />
        <FeatureSummary
            label="About Masked Domains"
            summary="This table displays all custom and masked domains for cobrands, including their domain, type, DNS, and SSL status. You can add, edit, or delete domains. This feature allows cobrands to use branded URLs and manage domain security for their users."
        />
        </>
    );
};

const moreIcon = { iconName: 'More' };

const ContextualMenu = ({ editOnClick, deleteOnClick, id, item, ...rest }) => {
    const menuProps = {
        items: [
            {
                key: 'print',
                iconProps: { iconName: 'Edit' },
                text: 'Edit',
                onClick: () => editOnClick(id),
            },
            {
                key: "Delete",
                iconProps: { iconName: "Delete" },
                text: "Delete",
                onClick: () => deleteOnClick(id, item?.domainMask || item?.cobrandName || item?.name || item?.slug || ""),
            },
        ],
    };

    return <IconButton iconProps={moreIcon} menuProps={menuProps} onRenderMenuIcon={() => {}} {...rest} />;
};

export default CustomDomainTable;
